import React, { useContext, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "context/AuthContext";
import Skeleton from "components/ui/Skeleton";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ScrollToTopBottom from "modules/swiftboard/components/ui/ScrollToTopBottom";
import ArticleHeader from "./article-view/ArticleHeader";
import InteractiveBlock from "./article-view/InteractiveBlock";
import RelatedArticles from "./article-view/RelatedArticles";
import AttachmentsList from "./article-view/AttachmentsList";
import useArticleInteractions from "./article-view/useArticleInteractions";
import apiKnowledgeHub from "./api";

const ArticleView = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const scrollRef = useRef(null);

  const {
    loading,
    article,
    fetchArticle,
    handleToggleBookmark,
    handleTogglePin,
    handleToggleLike,
    handleAddComment,
    handleDeleteComment,
    handlePinComment,
  } = useArticleInteractions(articleId);

  useEffect(() => {
    fetchArticle();
    apiKnowledgeHub.recordView(articleId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  const goBack = () => navigate("/swiftboard/knowledge-base");
  const openArticle = (id) => navigate(`/swiftboard/knowledge-base/${id}`);

  if (loading) {
    return (
      <div className="h-full overflow-y-auto bg-surface_alt">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="h-full overflow-y-auto bg-surface_alt flex items-center justify-center">
        <NoDataState
          icon="file"
          title="Article Not Found"
          message="This article may have been removed or is no longer available."
          showRefresh={false}
          showBack={true}
          onBack={goBack}
          size="medium"
        />
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <div ref={scrollRef} className="h-full overflow-y-auto bg-surface_alt">
        <ArticleHeader
          article={article}
          onBack={goBack}
          onToggleBookmark={handleToggleBookmark}
          onTogglePin={handleTogglePin}
        />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 min-w-0 break-normal [overflow-wrap:break-word] [word-break:normal] [hyphens:none]">
          <div className="space-y-4">
            {article.kb_contents.map((block) => (
              <InteractiveBlock
                key={block.id}
                block={block}
                currentUser={user}
                canModerate={article.can_moderate}
                onToggleLike={handleToggleLike}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onPinComment={handlePinComment}
                renderLikeExtra={(b) => {
                  const count = b.likes_preview?.total_likes ?? b.engagement?.total_likes ?? 0;
                  return count > 0 ? (
                    <span className="text-xs font-semibold text-text_muted">{count}</span>
                  ) : null;
                }}
              />
            ))}
          </div>

          <AttachmentsList attachments={article.kb_attachments} />
          <RelatedArticles articles={article.related_articles} onOpen={openArticle} />
        </div>
      </div>

      <ScrollToTopBottom scrollRef={scrollRef} />
    </div>
  );
};

export default ArticleView;
